import React, { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { db, auth, authService, signOut } from "../firebase";
import { billingService } from "../services/billingService";
import PromoCodeBlock from "../components/PromoCodeBlock";
import StatusBadge, { getStatusConfig } from "../components/StatusBadge";
import {
  doc,
  collection,
  query,
  where,
  getDocs,
  setDoc,
  updateDoc,
  increment,
  serverTimestamp,
} from "firebase/firestore";
import {
  ChevronLeft,
  ChevronRight,
  LayoutGrid,
  BookOpen,
  Scissors,
  Type,
  FileText,
  Users,
  Gift,
  Settings,
  HelpCircle,
  Sun,
  Moon,
  Flame,
} from "lucide-react";
import { ThemeProvider, useTheme } from "../components/ThemeProvider";
import {
  Tooltip,
} from "@mui/material";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  SidebarProvider,
  Sidebar,
  SidebarContent,
  SidebarHeader,
  SidebarFooter,
  SidebarMenu,
  SidebarMenuItem,
  SidebarMenuButton,
  SidebarInset,
  SidebarTrigger,
  SidebarGroup,
  SidebarGroupLabel,
  SidebarGroupContent,
} from "../components/ui/sidebar";
import OnboardingFlow from "../components/OnboardingFlow";
import Aurora from "../components/Aurora";
import Beams from "../components/home/Beams";
import "aos/dist/aos.css";

// Google Icon Component
const GoogleIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    className="h-5 w-5"
    viewBox="0 0 48 48"
  >
    <path
      fill="#FFC107"
      d="M43.611 20.083H42V20H24v8h11.303c-1.649 4.657-6.08 8-11.303 8-6.627 0-12-5.373-12-12s5.373-12 12-12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 12.955 4 4 12.955 4 24s8.955 20 20 20 20-8.955 20-20c0-2.641-.21-5.236-.611-7.743z"
    />
    <path
      fill="#FF3D00"
      d="M6.306 14.691l6.571 4.819C14.655 15.108 18.961 12 24 12c3.059 0 5.842 1.154 7.961 3.039l5.657-5.657C34.046 6.053 29.268 4 24 4 16.318 4 9.656 8.337 6.306 14.691z"
    />
    <path
      fill="#4CAF50"
      d="M24 44c5.166 0 9.86-1.977 13.409-5.192l-6.19-5.238C29.211 35.091 26.715 36 24 36c-5.202 0-9.619-3.317-11.283-7.946l-6.522 5.025C9.505 39.556 16.227 44 24 44z"
    />
    <path
      fill="#1976D2"
      d="M43.611 20.083H42V20H24v8h11.303c-.792 2.237-2.231 4.166-4.087 5.571l6.19 5.238C42.022 35.026 44 30.038 44 24c0-2.641-.21-5.236-.611-7.743z"
    />
  </svg>
);

// EduTools Logo Component (dark version for light backgrounds)
const EduToolsLogo = ({ className = "" }) => (
  <svg
    className={className}
    viewBox="0 0 222.68 211.78"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      fill="currentColor"
      d="M222.68,105.89c0,58.48-47.41,105.89-105.89,105.89h-5.45C49.86,211.78,2.45,164.37,2.45,105.89h0C2.45,47.41,49.86,0,108.34,0h5.45c58.48,0,105.89,47.41,105.89,105.89h0Z"
    />
    <path
      fill="white"
      d="M169.37,84.21c-3.71-2.88-9.06-2.21-11.94,1.5l-41.87,53.96-32.16-40.48c-2.85-3.59-8.09-4.19-11.68-1.34-3.59,2.85-4.19,8.09-1.34,11.68l38.26,48.16c1.53,1.93,3.86,3.06,6.33,3.08h.09c2.44,0,4.76-1.09,6.31-2.97l47.5-61.22c2.88-3.71,2.21-9.06-1.5-11.94v-.43Z"
    />
    <circle fill="white" cx="111.34" cy="62.39" r="15.5" />
  </svg>
);

// Slide images for the carousel
const slideImages = [
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-01_1-p-1600.png",
    title: "AI-Powered Study Tools",
    description: "Get instant help with any subject",
  },
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-07_1-p-1600.png",
    title: "Smart Flashcards",
    description: "Learn faster with personalized practice",
  },
  {
    src: process.env.PUBLIC_URL + "/images/Bento-Image-08_1-p-1600.png",
    title: "Collaborate with Friends",
    description: "Study together, succeed together",
  },
];

function HyperLinks() {
  const navigate = useNavigate();

  // Authentication state
  const [userEmail, setUserEmail] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [hasCompletedOnboarding, setHasCompletedOnboarding] = useState(false);
  const [googleUser, setGoogleUser] = useState(null); // Firebase Auth user from Google

  // Step management
  const [currentMainStep, setCurrentMainStep] = useState(0); // 0: login, 1-3: onboarding steps, 4: launchpad

  // Image slider state
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Settings and Profile modal state
  const [settingsModalOpen, setSettingsModalOpen] = useState(false);
  const [settingsTab, setSettingsTab] = useState("general");
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const [manageAccountModalOpen, setManageAccountModalOpen] = useState(false);
  const [accountTab, setAccountTab] = useState("account");
  const [billingPeriod, setBillingPeriod] = useState("monthly"); // "monthly" or "annual"

  // Login form state
  const [whitelistDocId, setWhitelistDocId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  // Onboarding form state
  const [formData, setFormData] = useState({
    fullName: "",
  });

  // User data
  const [userData, setUserData] = useState(null);
  const [checkingAccess, setCheckingAccess] = useState(true);

  // Helper: check trial/org expiration and downgrade to "free" if expired
  // Returns the resolved paymentStatus
  const checkAndHandleExpiration = async (email, whitelistDocRef, whitelistData) => {
    // Fetch latest paymentStatus from users collection
    let latestPaymentStatus = whitelistData.paymentStatus || "free";
    try {
      const usersRef = collection(db, "users");
      const userQuery = query(usersRef, where("email", "==", email));
      const userSnapshot = await getDocs(userQuery);

      if (!userSnapshot.empty) {
        const usersData = userSnapshot.docs[0].data();
        if (usersData.paymentStatus) {
          latestPaymentStatus = usersData.paymentStatus;
          console.log("[HyperLinks] ✓ Latest payment status from users collection:", latestPaymentStatus);
        }
      }
    } catch (error) {
      console.error("[HyperLinks] Error checking users collection for payment status:", error);
    }

    // Check if trial has expired
    if (latestPaymentStatus === "trial" && whitelistData.activePromoCode?.expiresAt) {
      const expiresAt = new Date(whitelistData.activePromoCode.expiresAt);
      const now = new Date();

      if (now > expiresAt) {
        console.log("[HyperLinks] ⏰ Trial has expired, updating to free");
        latestPaymentStatus = "free";

        try {
          await updateDoc(whitelistDocRef, {
            paymentStatus: "free",
            "activePromoCode.status": "expired",
          });

          const usersRef = collection(db, "users");
          const userQuery = query(usersRef, where("email", "==", email));
          const userSnapshot = await getDocs(userQuery);

          if (!userSnapshot.empty) {
            await updateDoc(userSnapshot.docs[0].ref, {
              paymentStatus: "free",
              "activePromoCode.status": "expired",
            });
          }

          console.log("[HyperLinks] ✅ Trial expiration synced to database");
        } catch (error) {
          console.error("[HyperLinks] Error updating expired trial:", error);
        }
      } else {
        const daysRemaining = Math.ceil((expiresAt - now) / (1000 * 60 * 60 * 24));
        console.log(`[HyperLinks] ✓ Trial active, ${daysRemaining} days remaining`);
      }
    }

    // Check if org access has expired (graduation date passed)
    if (latestPaymentStatus === "org" && whitelistData.activeOrgCode?.graduationDate) {
      const gradDate = new Date(whitelistData.activeOrgCode.graduationDate);
      const now = new Date();

      if (now > gradDate) {
        console.log("[HyperLinks] 🎓 Org access expired (graduation date passed), downgrading to free");
        latestPaymentStatus = "free";

        try {
          await updateDoc(whitelistDocRef, {
            paymentStatus: "free",
            "activeOrgCode.status": "expired",
          });

          const usersRef = collection(db, "users");
          const userQuery = query(usersRef, where("email", "==", email));
          const userSnapshot = await getDocs(userQuery);

          if (!userSnapshot.empty) {
            await updateDoc(userSnapshot.docs[0].ref, {
              paymentStatus: "free",
              "activeOrgCode.status": "expired",
            });
          }

          // Decrement inUse counters on the org code doc and parent org doc
          const orgCode = whitelistData.activeOrgCode;
          if (orgCode.orgId && orgCode.code) {
            const codeDocRef = doc(db, "orgs", orgCode.orgId, "codes", orgCode.code);
            const orgDocRef = doc(db, "orgs", orgCode.orgId);

            await updateDoc(codeDocRef, { inUse: increment(-1) });
            await updateDoc(orgDocRef, { inUse: increment(-1) });

            console.log("[HyperLinks] ✅ Decremented inUse for org and code docs");
          }

          console.log("[HyperLinks] ✅ Org expiration synced to database");
        } catch (error) {
          console.error("[HyperLinks] Error updating expired org access:", error);
        }
      } else {
        const daysRemaining = Math.ceil((gradDate - now) / (1000 * 60 * 60 * 24));
        console.log(`[HyperLinks] ✓ Org access active, ${daysRemaining} days remaining until graduation`);
      }
    }

    return latestPaymentStatus;
  };

  // Auto-advance slides for image carousel
  useEffect(() => {
    if (!isAutoPlaying || currentMainStep !== 0) return;
    const timer = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % slideImages.length);
    }, 5000);
    return () => clearInterval(timer);
  }, [isAutoPlaying, currentMainStep]);

  // Slide navigation
  const nextSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide((prev) => (prev + 1) % slideImages.length);
  };

  const prevSlide = () => {
    setIsAutoPlaying(false);
    setCurrentSlide(
      (prev) => (prev - 1 + slideImages.length) % slideImages.length,
    );
  };

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const authToken = localStorage.getItem("hyperlinksAuth");
      const storedEmail = localStorage.getItem("hyperlinksEmail");

      if (!authToken || !storedEmail) {
        // Not authenticated, stay on login step
        setCurrentMainStep(0);
        setCheckingAccess(false);
        return;
      }

      // User has auth token, verify and check onboarding
      setUserEmail(storedEmail);
      setIsAuthenticated(true);

      try {
        // Look up user by email in whitelist collection
        const whitelistRef = collection(db, "whitelist");
        const q = query(whitelistRef, where("email", "==", storedEmail));
        const querySnapshot = await getDocs(q);

        if (!querySnapshot.empty) {
          const whitelistDoc = querySnapshot.docs[0];
          const whitelistData = whitelistDoc.data();
          setWhitelistDocId(whitelistDoc.id);

          // Run expiration checks on reload
          const whitelistDocRef = doc(db, "whitelist", whitelistDoc.id);
          const resolvedStatus = await checkAndHandleExpiration(storedEmail, whitelistDocRef, whitelistData);
          const resolvedWhitelistData = resolvedStatus !== whitelistData.paymentStatus
            ? { ...whitelistData, paymentStatus: resolvedStatus }
            : whitelistData;
          setUserData(resolvedWhitelistData);

          // Check if user has completed onboarding in whitelist
          let onboardingCompleted =
            whitelistData.hyperlinksOnboardingCompleted ||
            whitelistData.onboardingCompleted;

          // ALSO check users collection for completed onboarding (Google OAuth users)
          if (!onboardingCompleted) {
            try {
              const usersRef = collection(db, "users");
              const userQuery = query(
                usersRef,
                where("email", "==", storedEmail),
              );
              const userSnapshot = await getDocs(userQuery);

              if (!userSnapshot.empty) {
                const userDoc = userSnapshot.docs[0];
                const userDataFromUsers = userDoc.data();

                // If user completed onboarding via Google OAuth, use that data
                if (
                  userDataFromUsers.onboardingCompleted &&
                  userDataFromUsers.profile
                ) {
                  onboardingCompleted = true;

                  // Sync onboarding data to whitelist
                  await updateDoc(doc(db, "whitelist", whitelistDoc.id), {
                    onboardingCompleted: true,
                    hyperlinksOnboardingCompleted: true,
                    profile: userDataFromUsers.profile,
                    googleUid: userDoc.id,
                  });

                  // Update local state with synced data
                  setUserData({
                    ...whitelistData,
                    onboardingCompleted: true,
                    profile: userDataFromUsers.profile,
                  });
                }
              }
            } catch (userCheckError) {
              console.error("Error checking users collection:", userCheckError);
            }
          }

          // Check if user has completed onboarding
          if (onboardingCompleted) {
            setHasCompletedOnboarding(true);
            setCurrentMainStep(4); // Go to dashboard (no paid requirement)
          } else {
            // Need to complete onboarding
            setCurrentMainStep(1); // Start onboarding
          }
        } else {
          // User not found in whitelist, clear auth
          localStorage.removeItem("hyperlinksAuth");
          localStorage.removeItem("hyperlinksEmail");
          setIsAuthenticated(false);
          setCurrentMainStep(0);
        }
      } catch (error) {
        console.error("Error checking user access:", error);
        setCurrentMainStep(0);
      } finally {
        setCheckingAccess(false);
      }
    };

    checkAuth();
  }, []);

  const handleOnboardingComplete = async (profileData) => {
    if (!userEmail || !whitelistDocId) {
      console.error(
        "[HyperLinks] Cannot complete onboarding - missing userEmail or whitelistDocId",
        {
          userEmail,
          whitelistDocId,
        },
      );
      setError("Unable to save onboarding data. Please try signing in again.");
      return;
    }

    console.log("[HyperLinks] Completing onboarding for user:", userEmail);
    setLoading(true);
    try {
      const whitelistDocRef = doc(db, "whitelist", whitelistDocId);

      console.log(
        "[HyperLinks] Saving profile data for",
        userEmail,
        ":",
        profileData,
      );

      // Update whitelist document with onboarding completed flag and form data
      await updateDoc(whitelistDocRef, {
        hyperlinksOnboardingCompleted: true,
        onboardingCompleted: true,
        onboardingDate: new Date().toISOString(),
        profile: profileData,
      });

      console.log(
        "[HyperLinks] ✓ Onboarding data saved to whitelist for:",
        userEmail,
      );

      // ALSO sync to users collection if Google account exists
      try {
        const usersRef = collection(db, "users");
        const userQuery = query(usersRef, where("email", "==", userEmail));
        const userSnapshot = await getDocs(userQuery);

        if (!userSnapshot.empty) {
          const userDocRef = doc(db, "users", userSnapshot.docs[0].id);
          await updateDoc(userDocRef, {
            onboardingCompleted: true,
            onboardingDate: new Date().toISOString(),
            profile: profileData,
          });
        }
      } catch (syncError) {
        console.error("Error syncing to users collection:", syncError);
        // Don't fail the whole operation if sync fails
      }

      setHasCompletedOnboarding(true);

      // Fetch updated user data
      const whitelistRef = collection(db, "whitelist");
      const q = query(whitelistRef, where("email", "==", userEmail));
      const querySnapshot = await getDocs(q);

      if (!querySnapshot.empty) {
        const whitelistData = querySnapshot.docs[0].data();
        setUserData(whitelistData);
      }

      // Redirect to dashboard
      setSuccess("Onboarding complete! Loading dashboard...");
      setTimeout(() => {
        setCurrentMainStep(4);
        setError("");
        setSuccess("");
      }, 1000);
    } catch (error) {
      console.error("Error completing onboarding:", error);
      setError("Error completing onboarding. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Google Sign-In Handler
  const handleGoogleSignIn = async () => {
    setLoading(true);
    setError("");
    setSuccess(window.__EDUTOOLS_PUPPETEER__ ? "Opening browser for sign-in..." : "Signing in...");

    try {
      console.log("[HyperLinks] Starting Google sign-in flow");
      const result = await authService.signInWithGoogle();

      if (!result || !result.user) {
        throw new Error("No user data received from authentication");
      }

      const user = result.user;
      const userEmailLower = user.email.toLowerCase();

      console.log("[HyperLinks] Authentication successful:", userEmailLower);
      console.log("[HyperLinks] User data:", {
        email: userEmailLower,
        name: user.displayName,
        uid: user.uid,
      });
      setSuccess("Sign-in successful! Setting up your account...");

      setGoogleUser(user);
      setUserEmail(userEmailLower);

      // Check if user exists in whitelist using email from Electron
      console.log(
        "[HyperLinks] Looking up user in Firestore by email:",
        userEmailLower,
      );
      const whitelistRef = collection(db, "whitelist");
      const q = query(whitelistRef, where("email", "==", userEmailLower));
      const querySnapshot = await getDocs(q);

      if (querySnapshot.empty) {
        // NEW USER: Create whitelist entry
        console.log(
          "[HyperLinks] ✓ New user - creating whitelist entry for:",
          userEmailLower,
        );

        // Check if user exists in users collection with payment status
        let paymentStatus = "free"; // Default
        try {
          const usersRef = collection(db, "users");
          const userQuery = query(
            usersRef,
            where("email", "==", userEmailLower),
          );
          const userSnapshot = await getDocs(userQuery);

          if (!userSnapshot.empty) {
            const existingUserData = userSnapshot.docs[0].data();
            if (existingUserData.paymentStatus) {
              paymentStatus = existingUserData.paymentStatus;
              console.log(
                "[HyperLinks] ✓ Found existing payment status in users collection:",
                paymentStatus,
              );
            }
          }
        } catch (error) {
          console.error("[HyperLinks] Error checking users collection:", error);
          // Continue with default "free" status
        }

        const newWhitelistRef = doc(collection(db, "whitelist"));

        const newUserData = {
          email: userEmailLower,
          googleUid: user.uid,
          displayName: user.displayName || userEmailLower,
          photoURL: user.photoURL || "",
          paymentStatus: paymentStatus, // Use from users collection or default "free"
          dateJoined: new Date().toISOString(),
          hyperlinksOnboardingCompleted: false,
          onboardingCompleted: false,
          authMethods: ["electron"],
          createdVia: "electron",
        };

        await setDoc(newWhitelistRef, newUserData);
        console.log(
          "[HyperLinks] ✓ New user created in whitelist:",
          newWhitelistRef.id,
          "with paymentStatus:",
          paymentStatus,
        );

        setWhitelistDocId(newWhitelistRef.id);

        // Create session
        const sessionToken =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        localStorage.setItem("hyperlinksAuth", sessionToken);
        localStorage.setItem("hyperlinksEmail", userEmailLower);

        setIsAuthenticated(true);
        setSuccess("Welcome! Let's get you set up...");

        // Pre-fill name in onboarding form
        setFormData((prev) => ({
          ...prev,
          fullName: user.displayName || userEmailLower.split("@")[0], // Use display name or email prefix
        }));

        // Go directly to onboarding
        setTimeout(() => {
          setCurrentMainStep(1);
          setSuccess("");
        }, 1000);
      } else {
        // EXISTING USER: Check onboarding status and proceed
        const whitelistDoc = querySnapshot.docs[0];
        const whitelistData = whitelistDoc.data();

        console.log("[HyperLinks] ✓ Existing user found:", {
          email: userEmailLower,
          docId: whitelistDoc.id,
          onboardingCompleted: whitelistData.hyperlinksOnboardingCompleted,
          currentPaymentStatus: whitelistData.paymentStatus,
        });

        setWhitelistDocId(whitelistDoc.id);
        setUserData(whitelistData);

        // Run expiration checks via shared helper
        const whitelistDocRef = doc(db, "whitelist", whitelistDoc.id);
        const latestPaymentStatus = await checkAndHandleExpiration(userEmailLower, whitelistDocRef, whitelistData);

        // Update whitelist with latest user data
        const updates = {};
        if (!whitelistData.googleUid) {
          updates.googleUid = user.uid;
        }
        if (!whitelistData.displayName && user.displayName) {
          updates.displayName = user.displayName;
        }
        if (!whitelistData.photoURL && user.photoURL) {
          updates.photoURL = user.photoURL;
        }
        // ALWAYS sync payment status from users collection
        if (whitelistData.paymentStatus !== latestPaymentStatus) {
          updates.paymentStatus = latestPaymentStatus;
          console.log(
            "[HyperLinks] 🔄 Syncing payment status:",
            whitelistData.paymentStatus,
            "→",
            latestPaymentStatus,
          );
        }

        if (Object.keys(updates).length > 0) {
          console.log("[HyperLinks] Updating whitelist data:", updates);
          await updateDoc(doc(db, "whitelist", whitelistDoc.id), updates);

          // Update local state with new payment status
          setUserData({
            ...whitelistData,
            ...updates,
          });
        }

        // Create session
        const sessionToken =
          Math.random().toString(36).substring(2, 15) +
          Math.random().toString(36).substring(2, 15);
        localStorage.setItem("hyperlinksAuth", sessionToken);
        localStorage.setItem("hyperlinksEmail", userEmailLower);

        setIsAuthenticated(true);

        // Check if they completed onboarding
        if (whitelistData.hyperlinksOnboardingCompleted) {
          console.log(
            "[HyperLinks] → User has completed onboarding, going to launchpad",
          );
          setHasCompletedOnboarding(true);
          setSuccess("Welcome back! Loading your workspace...");
          setTimeout(() => {
            setCurrentMainStep(4); // Go to launchpad
            setSuccess("");
          }, 1000);
        } else {
          console.log("[HyperLinks] → User needs to complete onboarding");

          // Pre-fill name in onboarding form
          setFormData((prev) => ({
            ...prev,
            fullName:
              user.displayName ||
              whitelistData.displayName ||
              userEmailLower.split("@")[0],
          }));

          setSuccess("Welcome back! Let's finish setting up your profile...");
          setTimeout(() => {
            setCurrentMainStep(1); // Start onboarding
            setSuccess("");
          }, 1000);
        }
      }
    } catch (error) {
      console.error("[HyperLinks] Error signing in with Google:", error);

      // Provide more specific error messages
      let errorMessage = "Failed to sign in. ";
      if (error.message && error.message.includes("Electron must pass")) {
        errorMessage +=
          "Please ensure you're using the latest version of the EduTools app.";
      } else if (error.message && error.message.includes("timeout")) {
        errorMessage += "Sign-in timed out. Please try again.";
      } else {
        errorMessage +=
          "Please try again or contact support if the problem persists.";
      }

      setError(errorMessage);
      setSuccess("");
    } finally {
      setLoading(false);
    }
  };

  // Logout handler
  const handleLogout = async () => {
    try {
      // Sign out from Firebase Auth if Google user
      if (auth.currentUser) {
        await signOut(auth);
      }

      localStorage.removeItem("hyperlinksAuth");
      localStorage.removeItem("hyperlinksEmail");
      setIsAuthenticated(false);
      setUserEmail(null);
      setUserData(null);
      setGoogleUser(null);
      setHasCompletedOnboarding(false);
      setCurrentMainStep(0);
      setFormData({
        fullName: "",
      });
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };


  // Show loading while checking access
  if (checkingAccess) {
    return (
      <div
        style={{
          backgroundColor: "#0c0c0c",
          minHeight: "100vh",
          position: "relative",
          overflow: "hidden",
        }}
      >
        {typeof window !== "undefined" && (
          <>
            <div
              style={{
                position: "absolute",
                top: "-20%",
                left: "-20%",
                width: "140%",
                height: "140%",
                zIndex: 0,
                pointerEvents: "none",
              }}
            >
              <Beams
                beamWidth={2.0}
                beamHeight={10}
                beamNumber={6}
                lightColor="#0080ff"
                speed={2.0}
                noiseIntensity={1.5}
                scale={0.18}
                rotation={20}
              />
            </div>
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "150px",
                background:
                  "linear-gradient(to bottom, #0c0c0c 0%, transparent 100%)",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />
            <div
              style={{
                position: "absolute",
                bottom: 0,
                left: 0,
                right: 0,
                height: "150px",
                background:
                  "linear-gradient(to top, #0c0c0c 0%, transparent 100%)",
                zIndex: 1,
                pointerEvents: "none",
              }}
            />
          </>
        )}

        <div
          style={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "100vh",
            padding: "40px 20px",
            position: "relative",
            zIndex: 2,
          }}
        >
          <h1
            style={{
              textAlign: "center",
              fontFamily: "'Inter', sans-serif",
              fontWeight: 700,
              fontSize: "2.5rem",
              color: "#ffffff",
              marginBottom: "20px",
            }}
          >
            Loading...
          </h1>
        </div>
      </div>
    );
  }

  // Render login steps (currentMainStep === 0)
  if (currentMainStep === 0) {
    return (
      <>
        <style>{`
          @keyframes spin {
            from { transform: rotate(0deg); }
            to { transform: rotate(360deg); }
          }
        `}</style>
        <div
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "#000000",
            position: "relative",
            overflow: "hidden",
          }}
        >
          {/* Authentication Form */}
          <div
            style={{
              width: "100%",
              maxWidth: "400px",
              padding: "48px",
              position: "relative",
              zIndex: 10,
            }}
          >
            <div style={{ width: "100%", maxWidth: "400px" }}>
              {/* Title and Description - Centered */}
              <h1
                style={{
                  fontSize: "1.75rem",
                  fontWeight: 600,
                  color: "#ffffff",
                  marginBottom: "8px",
                  whiteSpace: "nowrap",
                  textAlign: "center",
                }}
              >
                Welcome to EduTools
              </h1>
              <p
                style={{
                  color: "rgba(255,255,255,0.5)",
                  marginBottom: "24px",
                  fontSize: "14px",
                  textAlign: "center",
                }}
              >
                Your AI-powered study companion.
              </p>

              {/* Error Message */}
              {error && (
                <div
                  style={{
                    padding: "12px 16px",
                    marginBottom: "16px",
                    backgroundColor: "rgba(239, 68, 68, 0.1)",
                    border: "1px solid rgba(239, 68, 68, 0.3)",
                    borderRadius: "8px",
                    color: "#fca5a5",
                    fontSize: "14px",
                  }}
                >
                  {error}
                </div>
              )}

              {/* Success Message */}
              {success && !error && (
                <div
                  style={{
                    padding: "12px 16px",
                    marginBottom: "16px",
                    backgroundColor: "rgba(34, 197, 94, 0.1)",
                    border: "1px solid rgba(34, 197, 94, 0.3)",
                    borderRadius: "8px",
                    color: "#86efac",
                    fontSize: "14px",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                  }}
                >
                  <div
                    style={{
                      width: "16px",
                      height: "16px",
                      border: "2px solid currentColor",
                      borderTopColor: "transparent",
                      borderRadius: "50%",
                      animation: "spin 1s linear infinite",
                    }}
                  ></div>
                  {success}
                </div>
              )}

              {/* Continue with Google Button */}
              <button
                onClick={handleGoogleSignIn}
                disabled={loading}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  padding: "12px 16px",
                  border: "1px solid rgba(255,255,255,0.2)",
                  borderRadius: "6px",
                  backgroundColor: "transparent",
                  color: "#ffffff",
                  fontWeight: 400,
                  fontSize: "14px",
                  cursor: loading ? "not-allowed" : "pointer",
                  transition: "all 0.2s",
                  opacity: loading ? 0.7 : 1,
                }}
                onMouseEnter={(e) =>
                  !loading &&
                  (e.target.style.backgroundColor = "rgba(255,255,255,0.05)")
                }
                onMouseLeave={(e) =>
                  !loading && (e.target.style.backgroundColor = "transparent")
                }
              >
                <GoogleIcon />
                {loading ? "Loading..." : "Continue with Google"}
              </button>

              {/* Terms */}
              <p
                style={{
                  fontSize: "11px",
                  color: "rgba(255,255,255,0.4)",
                  marginTop: "20px",
                  textAlign: "center",
                }}
              >
                By continuing, you agree to our{" "}
                <Link
                  to="/terms-of-service"
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    textDecoration: "underline",
                  }}
                >
                  Terms of Service
                </Link>{" "}
                and{" "}
                <Link
                  to="/privacy-policy"
                  style={{
                    color: "rgba(255,255,255,0.6)",
                    textDecoration: "underline",
                  }}
                >
                  Privacy Policy
                </Link>
                .
              </p>

            </div>
          </div>
        </div>
      </>
    );
  }

  // Render onboarding steps (currentMainStep === 1, 2, 3)
  if (currentMainStep >= 1 && currentMainStep <= 3) {
    return (
      <OnboardingFlow
        onComplete={handleOnboardingComplete}
        userEmail={userEmail}
        initialData={{ fullName: formData.fullName }}
      />
    );
  }

  // Unified Settings & Account Modal Component
  const SettingsModal = () => {
    const { theme: currentTheme, setTheme } = useTheme();
    const isDark = currentTheme === "dark";

    if (!settingsModalOpen && !manageAccountModalOpen) return null;
    const c = {
      bg: isDark ? "#1a1a1e" : "#fafafa",
      sidebarBg: isDark ? "#141416" : "#f5f5f5",
      cardBg: isDark ? "#232328" : "#fff",
      border: isDark ? "#2e2e33" : "#e5e5e5",
      text: isDark ? "#e5e5e5" : "#000",
      textSecondary: isDark ? "#a0a0a5" : "#666",
      textMuted: isDark ? "#707075" : "#888",
      textDim: isDark ? "#606065" : "#999",
      textFeature: isDark ? "#ccc" : "#333",
      hoverBg: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.05)",
      inputBg: isDark ? "#2a2a30" : "#fff",
      inputFocusBorder: isDark ? "#888" : "#000",
      btnPrimaryBg: isDark ? "#e5e5e5" : "#1a1a1a",
      btnPrimaryText: isDark ? "#1a1a1a" : "#fff",
      btnSecondaryBg: isDark ? "#2a2a30" : "#f5f5f5",
      btnSecondaryText: isDark ? "#ccc" : "#333",
      btnDisabledBg: isDark ? "#2a2a30" : "#e5e5e5",
      btnDisabledText: isDark ? "#555" : "#999",
      avatarBg: isDark ? "#444" : "#1a1a1a",
      gradientBanner: isDark
        ? "linear-gradient(135deg, #1e1030 0%, #1a1828 50%, #1e1030 100%)"
        : "linear-gradient(135deg, #faf5ff 0%, #f5f3ff 50%, #faf5ff 100%)",
      progressTrack: isDark ? "#333" : "#e5e5e5",
      selectBg: isDark ? "#2a2a30" : "#fff",
      toggleBg: isDark ? "#232328" : "#fff",
    };

    // Parse user's full name into first and last
    const fullName = userData?.profile?.fullName || userData?.fullName || "";
    const nameParts = fullName.split(" ");
    const defaultFirstName = nameParts[0] || "";
    const defaultLastName = nameParts.slice(1).join(" ") || "";

    const settingsTabs = [
      {
        id: "general",
        label: "General",
        section: "settings",
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="4" y1="21" x2="4" y2="14" />
            <line x1="4" y1="10" x2="4" y2="3" />
            <line x1="12" y1="21" x2="12" y2="12" />
            <line x1="12" y1="8" x2="12" y2="3" />
            <line x1="20" y1="21" x2="20" y2="16" />
            <line x1="20" y1="12" x2="20" y2="3" />
            <line x1="1" y1="14" x2="7" y2="14" />
            <line x1="9" y1="8" x2="15" y2="8" />
            <line x1="17" y1="16" x2="23" y2="16" />
          </svg>
        ),
      },
    ];

    const accountTabs = [
      {
        id: "account",
        label: "Account",
        section: "account",
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
          </svg>
        ),
      },
      {
        id: "billing",
        label: "Billings and Plan",
        section: "account",
        icon: (
          <svg
            width="16"
            height="16"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        ),
        subItems: [{ id: "promo", label: "Access Code" }],
      },
    ];

    const allTabs = [...settingsTabs, ...accountTabs];
    const allSubItems = accountTabs.flatMap((t) => t.subItems || []);
    const currentTabData =
      allTabs.find((t) => t.id === settingsTab) ||
      allSubItems.find((t) => t.id === settingsTab);

    // Track which sidebar dropdowns are expanded
    const [expandedDropdowns, setExpandedDropdowns] = React.useState({});
    // Auto-expand billing dropdown when promo tab is active
    React.useEffect(() => {
      if (settingsTab === "promo") {
        setExpandedDropdowns((prev) => ({ ...prev, billing: true }));
      }
    }, [settingsTab]);
    const toggleDropdown = (tabId) => {
      setExpandedDropdowns((prev) => ({ ...prev, [tabId]: !prev[tabId] }));
    };
    const getTitle = () => {
      if (settingsTab === "account") return "Account";
      if (settingsTab === "promo") return "Access Code";
      return currentTabData?.label || "Settings";
    };

    const closeModal = () => {
      setSettingsModalOpen(false);
      setManageAccountModalOpen(false);
    };

    return (
      <div
        style={{
          position: "fixed",
          inset: 0,
          backgroundColor: "rgba(0, 0, 0, 0.5)",
          backdropFilter: "blur(4px)",
          zIndex: 9999,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
        onClick={closeModal}
      >
        <div
          style={{
            backgroundColor: c.bg,
            borderRadius: "16px",
            width: "900px",
            maxWidth: "92vw",
            height: "650px",
            maxHeight: "88vh",
            display: "flex",
            overflow: "hidden",
            boxShadow: "0 25px 50px -12px rgba(0, 0, 0, 0.4)",
            border: isDark ? "1px solid #2e2e33" : "none",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Sidebar */}
          <div
            style={{
              width: "220px",
              backgroundColor: c.sidebarBg,
              padding: "20px 12px",
              borderRight: `1px solid ${c.border}`,
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Settings Section */}
            <p
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: c.textMuted,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginBottom: "8px",
                paddingLeft: "12px",
              }}
            >
              Settings
            </p>
            {settingsTabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setSettingsTab(tab.id)}
                style={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: "10px",
                  padding: "10px 12px",
                  marginBottom: "2px",
                  border: "none",
                  borderRadius: "8px",
                  backgroundColor:
                    settingsTab === tab.id ? c.cardBg : "transparent",
                  color: settingsTab === tab.id ? c.text : c.textSecondary,
                  fontSize: "14px",
                  fontWeight: settingsTab === tab.id ? 500 : 400,
                  cursor: "pointer",
                  textAlign: "left",
                  boxShadow:
                    settingsTab === tab.id
                      ? isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.08)"
                      : "none",
                }}
              >
                {tab.icon}
                {tab.label}
              </button>
            ))}

            {/* Account Section */}
            <p
              style={{
                fontSize: "11px",
                fontWeight: 600,
                color: c.textMuted,
                textTransform: "uppercase",
                letterSpacing: "0.5px",
                marginTop: "20px",
                marginBottom: "8px",
                paddingLeft: "12px",
              }}
            >
              Account
            </p>
            {accountTabs.map((tab) => {
              const isActive = settingsTab === tab.id || tab.subItems?.some((sub) => sub.id === settingsTab);
              const hasSubItems = tab.subItems && tab.subItems.length > 0;
              const isExpanded = hasSubItems && (expandedDropdowns[tab.id] || false);
              return (
              <div key={tab.id}>
                <button
                  onClick={() => {
                    setSettingsTab(tab.id);
                    if (hasSubItems) toggleDropdown(tab.id);
                  }}
                  style={{
                    width: "100%",
                    display: "flex",
                    alignItems: "center",
                    gap: "10px",
                    padding: "10px 12px",
                    marginBottom: "2px",
                    border: "none",
                    borderRadius: "8px",
                    backgroundColor: isActive ? c.cardBg : "transparent",
                    color: isActive ? c.text : c.textSecondary,
                    fontSize: "14px",
                    fontWeight: isActive ? 500 : 400,
                    cursor: "pointer",
                    textAlign: "left",
                    boxShadow: isActive
                      ? isDark ? "0 1px 3px rgba(0,0,0,0.3)" : "0 1px 3px rgba(0,0,0,0.08)"
                      : "none",
                  }}
                >
                  {tab.icon}
                  <span style={{ flex: 1 }}>{tab.label}</span>
                  {hasSubItems && (
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      style={{
                        flexShrink: 0,
                        transform: isExpanded ? "rotate(180deg)" : "rotate(0deg)",
                        transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
                      }}
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  )}
                </button>
                {/* Sub-items dropdown */}
                {hasSubItems && (
                    <div
                      style={{
                        marginLeft: "26px",
                        marginTop: "4px",
                        marginBottom: "4px",
                        paddingTop: "4px",
                        overflow: "hidden",
                        maxHeight: isExpanded ? `${tab.subItems.length * 40}px` : "0px",
                        opacity: isExpanded ? 1 : 0,
                        transition: "max-height 0.25s cubic-bezier(0.4, 0, 0.2, 1), opacity 0.2s ease",
                      }}
                    >
                      {tab.subItems.map((subItem) => (
                        <button
                          key={subItem.id}
                          onClick={() => setSettingsTab(subItem.id)}
                          style={{
                            width: "100%",
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            padding: "8px 12px",
                            marginBottom: "2px",
                            border: "none",
                            borderRadius: "6px",
                            backgroundColor:
                              settingsTab === subItem.id
                                ? c.hoverBg
                                : "transparent",
                            color: settingsTab === subItem.id ? c.text : c.textSecondary,
                            fontSize: "13px",
                            fontWeight: settingsTab === subItem.id ? 500 : 400,
                            cursor: "pointer",
                            textAlign: "left",
                          }}
                        >
                          {subItem.label}
                        </button>
                      ))}
                    </div>
                  )}
              </div>
              );
            })}

            {/* Version at bottom */}
            <div
              style={{
                marginTop: "auto",
                paddingTop: "20px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                paddingLeft: "12px",
                paddingRight: "12px",
              }}
            >
              <span style={{ fontSize: "12px", color: c.textDim }}>
                EduTools v1.0.0
              </span>
            </div>
          </div>

          {/* Content */}
          <div
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            {/* Scrollable Content */}
            <div style={{ flex: 1, padding: "24px 32px", overflowY: "auto" }}>
              {/* Header */}
              {settingsTab !== "promo" && (
                <div style={{ marginBottom: "28px" }}>
                  <h2
                    style={{
                      fontSize: "18px",
                      fontWeight: 500,
                      color: c.text,
                      margin: 0,
                      letterSpacing: "-0.01em",
                    }}
                  >
                    {getTitle()}
                  </h2>
                </div>
              )}
              {/* Account Tab */}
              {settingsTab === "account" && (
                <div>
                  <div
                    style={{
                      backgroundColor: c.cardBg,
                      border: `1px solid ${c.border}`,
                      borderRadius: "12px",
                      overflow: "hidden",
                    }}
                  >
                    <div
                      style={{
                        padding: "16px 20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: `1px solid ${c.border}`,
                      }}
                    >
                      <span style={{ fontSize: "14px", color: c.textSecondary }}>
                        First name
                      </span>
                      <input
                        type="text"
                        defaultValue={defaultFirstName}
                        style={{
                          padding: "8px 12px",
                          border: `1px solid ${c.border}`,
                          borderRadius: "8px",
                          fontSize: "14px",
                          width: "200px",
                          textAlign: "right",
                          outline: "none",
                          backgroundColor: c.inputBg,
                          color: c.text,
                        }}
                        onFocus={(e) => (e.target.style.borderColor = c.inputFocusBorder)}
                        onBlur={(e) => (e.target.style.borderColor = c.border)}
                      />
                    </div>
                    <div
                      style={{
                        padding: "16px 20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: `1px solid ${c.border}`,
                      }}
                    >
                      <span style={{ fontSize: "14px", color: c.textSecondary }}>
                        Last name
                      </span>
                      <input
                        type="text"
                        defaultValue={defaultLastName}
                        style={{
                          padding: "8px 12px",
                          border: `1px solid ${c.border}`,
                          borderRadius: "8px",
                          fontSize: "14px",
                          width: "200px",
                          textAlign: "right",
                          outline: "none",
                          backgroundColor: c.inputBg,
                          color: c.text,
                        }}
                        onFocus={(e) => (e.target.style.borderColor = c.inputFocusBorder)}
                        onBlur={(e) => (e.target.style.borderColor = c.border)}
                      />
                    </div>
                    <div
                      style={{
                        padding: "16px 20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        borderBottom: `1px solid ${c.border}`,
                      }}
                    >
                      <span style={{ fontSize: "14px", color: c.textSecondary }}>
                        Email
                      </span>
                      <span style={{ fontSize: "14px", color: c.text }}>
                        {userEmail || "—"}
                      </span>
                    </div>
                    <div
                      style={{
                        padding: "16px 20px",
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <span style={{ fontSize: "14px", color: c.textSecondary }}>
                        Profile picture
                      </span>
                      <div
                        style={{
                          width: "40px",
                          height: "40px",
                          borderRadius: "50%",
                          backgroundColor: c.avatarBg,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        <span
                          style={{
                            fontSize: "16px",
                            fontWeight: 500,
                            color: "#fff",
                          }}
                        >
                          {userEmail?.charAt(0)?.toUpperCase() || "U"}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Bottom Buttons */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      marginTop: "24px",
                    }}
                  >
                    <div style={{ display: "flex", gap: "12px" }}>
                      <button
                        onClick={handleLogout}
                        style={{
                          padding: "10px 20px",
                          backgroundColor: c.btnPrimaryBg,
                          color: c.btnPrimaryText,
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Sign out
                      </button>
                      <button
                        style={{
                          padding: "10px 20px",
                          backgroundColor: "transparent",
                          color: c.textSecondary,
                          border: "none",
                          borderRadius: "8px",
                          fontSize: "14px",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Delete account
                      </button>
                    </div>
                    <button
                      style={{
                        padding: "10px 24px",
                        backgroundColor: c.btnDisabledBg,
                        color: c.btnDisabledText,
                        border: "none",
                        borderRadius: "8px",
                        fontSize: "14px",
                        fontWeight: 500,
                        cursor: "not-allowed",
                      }}
                      disabled
                    >
                      Save
                    </button>
                  </div>
                </div>
              )}

              {/* General Tab */}
              {settingsTab === "general" && (
                <div
                  style={{
                    backgroundColor: c.cardBg,
                    border: `1px solid ${c.border}`,
                    borderRadius: "12px",
                    padding: "20px",
                  }}
                >
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                      marginBottom: "16px",
                    }}
                  >
                    <div>
                      <p
                        style={{
                          fontSize: "14px",
                          fontWeight: 500,
                          color: c.text,
                          margin: 0,
                        }}
                      >
                        Theme
                      </p>
                      <p
                        style={{
                          fontSize: "13px",
                          color: c.textSecondary,
                          margin: "4px 0 0 0",
                        }}
                      >
                        Choose your preferred appearance
                      </p>
                    </div>
                    <select
                      value={currentTheme}
                      onChange={(e) => setTheme(e.target.value)}
                      style={{
                        padding: "8px 12px",
                        border: `1px solid ${c.border}`,
                        borderRadius: "8px",
                        fontSize: "14px",
                        backgroundColor: c.selectBg,
                        color: c.text,
                      }}
                    >
                      <option value="light">Light</option>
                      <option value="dark">Dark</option>
                    </select>
                  </div>
                  <div
                    style={{
                      borderTop: `1px solid ${c.border}`,
                      paddingTop: "16px",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <p
                          style={{
                            fontSize: "14px",
                            fontWeight: 500,
                            color: c.text,
                            margin: 0,
                          }}
                        >
                          Language
                        </p>
                        <p
                          style={{
                            fontSize: "13px",
                            color: c.textSecondary,
                            margin: "4px 0 0 0",
                          }}
                        >
                          Select your language
                        </p>
                      </div>
                      <select
                        style={{
                          padding: "8px 12px",
                          border: `1px solid ${c.border}`,
                          borderRadius: "8px",
                          fontSize: "14px",
                          backgroundColor: c.selectBg,
                          color: c.text,
                        }}
                      >
                        <option>English</option>
                        <option>Spanish</option>
                        <option>French</option>
                      </select>
                    </div>
                  </div>
                </div>
              )}

              {/* Plans and Billing Tab */}
              {settingsTab === "billing" && (
                <div>
                  {/* Monthly/Annual Toggle */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: "20px",
                    }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        backgroundColor: c.toggleBg,
                        border: `1px solid ${c.border}`,
                        borderRadius: "20px",
                        padding: "3px",
                      }}
                    >
                      <button
                        onClick={() => setBillingPeriod("monthly")}
                        style={{
                          padding: "6px 20px",
                          backgroundColor: "transparent",
                          color: billingPeriod === "monthly" ? c.text : c.textMuted,
                          border:
                            billingPeriod === "monthly"
                              ? `1px solid ${c.border}`
                              : "1px solid transparent",
                          borderRadius: "16px",
                          fontSize: "13px",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Monthly
                      </button>
                      <button
                        onClick={() => setBillingPeriod("annual")}
                        style={{
                          padding: "6px 20px",
                          backgroundColor: "transparent",
                          color: billingPeriod === "annual" ? c.text : c.textMuted,
                          border:
                            billingPeriod === "annual"
                              ? `1px solid ${c.border}`
                              : "1px solid transparent",
                          borderRadius: "16px",
                          fontSize: "13px",
                          fontWeight: 500,
                          cursor: "pointer",
                        }}
                      >
                        Annual
                      </button>
                    </div>
                  </div>

                  {/* Pricing Table */}
                  <div
                    style={{
                      backgroundColor: c.cardBg,
                      border: `1px solid ${c.border}`,
                      borderRadius: "12px",
                      overflow: "hidden",
                      marginBottom: "20px",
                      boxShadow: isDark ? "0 4px 12px rgba(0, 0, 0, 0.2)" : "0 4px 12px rgba(0, 0, 0, 0.06)",
                    }}
                  >
                    {/* Header Row */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        borderBottom: `1px solid ${c.border}`,
                      }}
                    >
                      {/* Pro Header */}
                      <div
                        style={{
                          padding: "18px 24px",
                          borderRight: `1px solid ${c.border}`,
                        }}
                      >
                        <p
                          style={{
                            fontSize: "12px",
                            color: c.textMuted,
                            margin: "0 0 6px 0",
                          }}
                        >
                          For individuals and teams
                        </p>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "8px",
                            marginBottom: "4px",
                          }}
                        >
                          <p
                            style={{
                              fontSize: "17px",
                              fontWeight: 600,
                              color: c.text,
                              margin: 0,
                            }}
                          >
                            Pro
                          </p>
                          {billingPeriod === "annual" && (
                            <span
                              style={{
                                backgroundColor: "#a855f7",
                                color: "#fff",
                                padding: "2px 8px",
                                borderRadius: "4px",
                                fontSize: "11px",
                                fontWeight: 600,
                              }}
                            >
                              -38%
                            </span>
                          )}
                        </div>
                        <p
                          style={{ fontSize: "13px", color: c.textSecondary, margin: 0 }}
                        >
                          {billingPeriod === "monthly"
                            ? "$19.99 per user/mo"
                            : "$12.50 per user/mo"}
                        </p>
                      </div>
                      {/* Enterprise Header */}
                      <div style={{ padding: "18px 24px" }}>
                        <p
                          style={{
                            fontSize: "12px",
                            color: c.textMuted,
                            margin: "0 0 6px 0",
                          }}
                        >
                          For teams with advanced needs
                        </p>
                        <p
                          style={{
                            fontSize: "17px",
                            fontWeight: 600,
                            color: c.text,
                            margin: "0 0 4px 0",
                          }}
                        >
                          Enterprise
                        </p>
                        <p
                          style={{ fontSize: "13px", color: c.textSecondary, margin: 0 }}
                        >
                          {billingPeriod === "monthly"
                            ? "$39.99 per user/mo"
                            : "$29.99 per user/mo"}
                        </p>
                      </div>
                    </div>

                    {/* Features Row */}
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                      }}
                    >
                      {/* Pro Features */}
                      <div
                        style={{
                          padding: "18px 24px",
                          borderRight: `1px solid ${c.border}`,
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          {[
                            "Access to all basic features",
                            "Access all your favorite premium tools",
                            "High speed desktop app",
                            "24/7 priority chat support",
                            "New tool suggestion requests",
                            "Early access to new features",
                          ].map((feature, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "10px",
                                marginBottom: "12px",
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#10b981"
                                strokeWidth="2.5"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: c.textFeature,
                                  lineHeight: "1.4",
                                }}
                              >
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              console.log('[HyperLinks] Upgrade to Pro clicked');
                              await billingService.upgradeToPlan('pro', billingPeriod);
                              // Success! Update UI or show confirmation
                              alert('Successfully upgraded to Pro!');
                            } catch (error) {
                              console.error('[HyperLinks] Upgrade failed:', error);
                              alert('Upgrade failed. Please try again.');
                            }
                          }}
                          style={{
                            width: "100%",
                            marginTop: "16px",
                            padding: "12px 20px",
                            backgroundColor: c.btnPrimaryBg,
                            color: c.btnPrimaryText,
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                        >
                          Upgrade to Pro
                        </button>
                        <div style={{ height: "28px" }} />
                      </div>
                      {/* Enterprise Features */}
                      <div
                        style={{
                          padding: "18px 24px",
                          display: "flex",
                          flexDirection: "column",
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          {[
                            "Dedicated support",
                            "SSO / SAML",
                            "Custom integrations",
                            "Advanced analytics",
                            "Team collaboration features",
                            "Bulk pricing discounts",
                          ].map((feature, i) => (
                            <div
                              key={i}
                              style={{
                                display: "flex",
                                alignItems: "flex-start",
                                gap: "10px",
                                marginBottom: "12px",
                              }}
                            >
                              <svg
                                width="14"
                                height="14"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="#10b981"
                                strokeWidth="2.5"
                                style={{ flexShrink: 0, marginTop: "2px" }}
                              >
                                <polyline points="20 6 9 17 4 12" />
                              </svg>
                              <span
                                style={{
                                  fontSize: "12px",
                                  color: c.textFeature,
                                  lineHeight: "1.4",
                                }}
                              >
                                {feature}
                              </span>
                            </div>
                          ))}
                        </div>
                        <button
                          onClick={async () => {
                            try {
                              console.log('[HyperLinks] Enterprise upgrade clicked');
                              await billingService.upgradeToPlan('enterprise', billingPeriod);
                              alert('Successfully upgraded to Enterprise!');
                            } catch (error) {
                              console.error('[HyperLinks] Enterprise upgrade failed:', error);
                              alert('Upgrade failed. Please try again.');
                            }
                          }}
                          style={{
                            width: "100%",
                            marginTop: "16px",
                            padding: "12px 20px",
                            backgroundColor: c.btnSecondaryBg,
                            color: c.btnSecondaryText,
                            border: "none",
                            borderRadius: "8px",
                            fontSize: "13px",
                            fontWeight: 500,
                            cursor: "pointer",
                          }}
                        >
                          Let's Talk
                        </button>
                        <p
                          style={{
                            fontSize: "11px",
                            color: c.textMuted,
                            textAlign: "center",
                            margin: "10px 0 0 0",
                          }}
                        >
                          Have an access code?{" "}
                          <button
                            onClick={() => {
                              setSettingsTab("promo");
                              setExpandedDropdowns((prev) => ({ ...prev, billing: true }));
                            }}
                            style={{
                              background: "none",
                              border: "none",
                              padding: 0,
                              fontSize: "11px",
                              fontWeight: 600,
                              color: "#7c3aed",
                              cursor: "pointer",
                              textDecoration: "underline",
                              textUnderlineOffset: "2px",
                            }}
                          >
                            Click here
                          </button>
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Dynamic Plan Banner — changes based on paymentStatus */}
                  <div
                    style={{
                      background: c.gradientBanner,
                      borderRadius: "12px",
                      padding: "20px",
                      marginBottom: "16px",
                    }}
                  >
                    {(() => {
                      const status = userData?.paymentStatus || "free";
                      const statusConfig = getStatusConfig(status);

                      if (status === "paid") {
                        /* Paid users: show "Current Plan" with subscription info */
                        return (
                          <>
                            <p style={{ fontSize: "16px", fontWeight: 600, color: c.text, margin: "0 0 10px 0" }}>
                              {statusConfig.planName}
                            </p>
                            <p style={{ fontSize: "13px", color: c.textSecondary, margin: "0 0 8px 0" }}>
                              You have full access to all Pro features.
                            </p>
                            {userData?.plan && (
                              <p style={{ fontSize: "12px", color: c.textSecondary, margin: 0 }}>
                                Plan: {userData.plan === "annual" ? "Annual" : "Monthly"}
                                {userData.subscriptionEndDate && (
                                  <> · Renews {new Date(userData.subscriptionEndDate).toLocaleDateString()}</>
                                )}
                              </p>
                            )}
                          </>
                        );
                      }

                      if (status === "org") {
                        /* Org users: show "Current Plan" with org name and graduation expiry */
                        return (
                          <>
                            <p style={{ fontSize: "16px", fontWeight: 600, color: c.text, margin: "0 0 10px 0" }}>
                              {statusConfig.planName}
                            </p>
                            <p style={{ fontSize: "13px", color: c.textSecondary, margin: "0 0 8px 0" }}>
                              You have full access via {userData?.activeOrgCode?.organizationName || "your organization"}.
                            </p>
                            {userData?.activeOrgCode?.graduationDate && (
                              <p style={{ fontSize: "12px", color: c.textSecondary, margin: 0 }}>
                                Access expires: {new Date(userData.activeOrgCode.graduationDate).toLocaleDateString()}
                              </p>
                            )}
                          </>
                        );
                      }

                      if (status === "trial") {
                        /* Trial users: show trial info with expiry and upgrade button */
                        const expiresAt = userData?.activePromoCode?.expiresAt;
                        const daysLeft = expiresAt
                          ? Math.max(0, Math.ceil((new Date(expiresAt) - new Date()) / (1000 * 60 * 60 * 24)))
                          : null;
                        return (
                          <>
                            <p style={{ fontSize: "16px", fontWeight: 600, color: c.text, margin: "0 0 10px 0" }}>
                              {statusConfig.planName}
                            </p>
                            {daysLeft !== null && (
                              <>
                                <div style={{ height: "5px", backgroundColor: c.progressTrack, borderRadius: "3px", marginBottom: "10px", overflow: "hidden" }}>
                                  <div style={{ width: `${Math.min(100, Math.max(5, (daysLeft / 30) * 100))}%`, height: "100%", background: "linear-gradient(90deg, #3b82f6, #60a5fa)", borderRadius: "3px" }} />
                                </div>
                                <p style={{ fontSize: "13px", color: c.textSecondary, margin: "0 0 16px 0" }}>
                                  {daysLeft > 0 ? `${daysLeft} day${daysLeft !== 1 ? "s" : ""} remaining` : "Trial expired"}
                                </p>
                              </>
                            )}
                            <div style={{ display: "flex", gap: "10px" }}>
                              <button
                                onClick={async () => {
                                  try {
                                    await billingService.upgradeToPlan('pro', billingPeriod);
                                  } catch (error) {
                                    console.error('[HyperLinks] Upgrade failed:', error);
                                  }
                                }}
                                style={{ padding: "10px 20px", backgroundColor: c.btnPrimaryBg, color: c.btnPrimaryText, border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
                              >
                                Upgrade to Pro
                              </button>
                            </div>
                          </>
                        );
                      }

                      /* Free users: original behavior with upgrade button */
                      return (
                        <>
                          <p style={{ fontSize: "16px", fontWeight: 600, color: c.text, margin: "0 0 10px 0" }}>
                            {statusConfig.planName}
                          </p>
                          <div style={{ height: "5px", backgroundColor: c.progressTrack, borderRadius: "3px", marginBottom: "10px", overflow: "hidden" }}>
                            <div style={{ width: "45%", height: "100%", background: "linear-gradient(90deg, #7c3aed, #a78bfa)", borderRadius: "3px" }} />
                          </div>
                          <p style={{ fontSize: "13px", color: c.textSecondary, margin: "0 0 16px 0" }}>
                            Unlimited access to basic features.
                          </p>
                          <div style={{ display: "flex", gap: "10px" }}>
                            <button
                              onClick={async () => {
                                try {
                                  console.log('[HyperLinks] Free Plan upgrade clicked');
                                  await billingService.upgradeToPlan('pro', billingPeriod);
                                  alert('Successfully upgraded to Pro!');
                                } catch (error) {
                                  console.error('[HyperLinks] Upgrade failed:', error);
                                  alert('Upgrade failed. Please try again.');
                                }
                              }}
                              style={{ padding: "10px 20px", backgroundColor: c.btnPrimaryBg, color: c.btnPrimaryText, border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
                            >
                              Upgrade to Pro
                            </button>
                            <button
                              style={{ padding: "10px 20px", backgroundColor: c.btnSecondaryBg, color: c.textSecondary, border: "none", borderRadius: "8px", fontSize: "13px", fontWeight: 500, cursor: "pointer" }}
                            >
                              Explore features
                            </button>
                          </div>
                        </>
                      );
                    })()}
                  </div>

                  {/* Footer Links */}
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <button
                      style={{
                        background: "none",
                        border: "none",
                        fontSize: "13px",
                        fontWeight: 500,
                        color: c.textFeature,
                        cursor: "pointer",
                        padding: 0,
                      }}
                    >
                      Billing and Plans FAQ
                    </button>
                    <p style={{ fontSize: "13px", color: c.textSecondary, margin: 0 }}>
                      Questions about billing?{" "}
                      <a
                        href="mailto:contact@edutools.app"
                        style={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: c.textFeature,
                          cursor: "pointer",
                          textDecoration: "none",
                        }}
                      >
                        Contact us
                      </a>
                    </p>
                  </div>
                </div>
              )}

              {/* Promo Code Tab */}
              {settingsTab === "promo" && (
                <div
                  style={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    minHeight: "100%",
                  }}
                >
                  <PromoCodeBlock
                    userEmail={userEmail || googleUser?.email}
                    userId={googleUser?.uid}
                    graduationMonth={userData?.graduationMonth}
                    graduationYear={userData?.graduationYear}
                  />
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  // ManageAccountModal now just opens the SettingsModal with account tab selected
  const ManageAccountModal = () => null;

  // Profile Dropdown Component
  const ProfileDropdown = () => {
    const { theme: currentTheme } = useTheme();
    const dark = currentTheme === "dark";
    const p = {
      bg: dark ? "#1a1a1e" : "#fff",
      border: dark ? "#2e2e33" : "#e8e8e8",
      divider: dark ? "#2a2a30" : "#f0f0f0",
      text: dark ? "#e5e5e5" : "#000",
      textSecondary: dark ? "#a0a0a5" : "#666",
      textMuted: dark ? "#707075" : "#888",
      textFeature: dark ? "#ccc" : "#333",
      avatarBg: dark ? "#444" : "#1a1a1a",
      btnBg: dark ? "#2a2a30" : "#fff",
      btnBorder: dark ? "#3a3a40" : "#e0e0e0",
      btnHover: dark ? "#333338" : "#f5f5f5",
      hoverBg: dark ? "#232328" : "#f5f5f5",
      iconColor: dark ? "#a0a0a5" : "#666",
    };

    if (!profileDropdownOpen) return null;

    return (
      <>
        {/* Click-away overlay */}
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9998,
          }}
          onClick={(e) => {
            e.stopPropagation();
            setProfileDropdownOpen(false);
          }}
        />
        <div
          style={{
            position: "absolute",
            top: "calc(100% + 12px)",
            right: "-8px",
            backgroundColor: p.bg,
            borderRadius: "16px",
            boxShadow: dark
              ? "0 20px 60px rgba(0, 0, 0, 0.5)"
              : "0 20px 60px rgba(0, 0, 0, 0.2)",
            border: `1px solid ${p.border}`,
            width: "340px",
            zIndex: 9999,
            overflow: "hidden",
          }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* User Info Header */}
          <div style={{ padding: "20px", borderBottom: `1px solid ${p.divider}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div
                style={{
                  width: "56px",
                  height: "56px",
                  borderRadius: "50%",
                  backgroundColor: p.avatarBg,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <span
                  style={{ fontSize: "22px", fontWeight: 600, color: "#fff" }}
                >
                  {userEmail?.charAt(0)?.toUpperCase() || "U"}
                </span>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  <p
                    style={{
                      fontSize: "16px",
                      fontWeight: 600,
                      color: p.text,
                      margin: 0,
                    }}
                  >
                    {userData?.profile?.fullName ||
                      userData?.fullName ||
                      "User"}
                  </p>
                  {/* Dynamic status badge in profile dropdown */}
                  <StatusBadge paymentStatus={userData?.paymentStatus} />
                </div>
                <p
                  style={{
                    fontSize: "14px",
                    color: p.textSecondary,
                    margin: "4px 0 0 0",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {userEmail}
                </p>
              </div>
            </div>
          </div>

          {/* Referral Section */}
          <div
            style={{ padding: "16px 20px", borderBottom: `1px solid ${p.divider}` }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div>
                <p
                  style={{
                    fontSize: "14px",
                    fontWeight: 600,
                    color: p.text,
                    margin: 0,
                  }}
                >
                  Get a free month of Pro
                </p>
                <p
                  style={{
                    fontSize: "13px",
                    color: p.textMuted,
                    margin: "2px 0 0 0",
                  }}
                >
                  Refer friends, earn rewards
                </p>
              </div>
              <button
                style={{
                  padding: "8px 16px",
                  backgroundColor: p.btnBg,
                  border: `1px solid ${p.btnBorder}`,
                  borderRadius: "8px",
                  fontSize: "13px",
                  fontWeight: 500,
                  color: p.text,
                  cursor: "pointer",
                  transition: "all 0.2s",
                }}
                onMouseEnter={(e) =>
                  (e.currentTarget.style.backgroundColor = p.btnHover)
                }
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor = p.btnBg)
                }
              >
                Refer a friend
              </button>
            </div>
          </div>

          {/* Manage Account */}
          <div style={{ padding: "8px 12px" }}>
            <button
              onClick={() => {
                setProfileDropdownOpen(false);
                setSettingsTab("account");
                setSettingsModalOpen(true);
              }}
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                gap: "12px",
                padding: "12px",
                border: "none",
                borderRadius: "8px",
                backgroundColor: "transparent",
                color: p.text,
                fontSize: "14px",
                fontWeight: 500,
                cursor: "pointer",
                textAlign: "left",
              }}
              onMouseEnter={(e) =>
                (e.currentTarget.style.backgroundColor = p.hoverBg)
              }
              onMouseLeave={(e) =>
                (e.currentTarget.style.backgroundColor = "transparent")
              }
            >
              Manage account
            </button>
          </div>
        </div>
      </>
    );
  };

  // Render dashboard (currentMainStep === 4)
  const services = [
    {
      id: 1,
      name: "ChatGPT",
      logo: "/logos/chatgpt.png",
      link: "https://chatgpt.com/",
      description: "AI-powered writing and assistance tool.",
    },
    {
      id: 2,
      name: "Quizlet",
      logo: "/logos/quizlet.png",
      link: "https://quizlet.com",
      description: "Flashcards and study tools.",
    },
    {
      id: 3,
      name: "Canva",
      logo: "/logos/canva.png",
      link: "https://www.canva.com/",
      description: "Graphic design made simple.",
    },
    {
      id: 4,
      name: "CourseHero",
      logo: "/logos/coursehero.png",
      link: "https://www.coursehero.com/",
      description: "Study resources and course materials.",
    },
    {
      id: 5,
      name: "Grammarly",
      logo: "/logos/grammarly.png",
      link: "https://app.grammarly.com/",
      description: "Grammar and spelling checker.",
    },
    {
      id: 6,
      name: "Symbolab",
      logo: "/logos/symbolab.png",
      link: "https://www.symbolab.com/",
      description: "Math problem solver.",
    },
    {
      id: 7,
      name: "Chegg",
      logo: "/logos/chegg.png",
      link: "https://www.chegg.com/",
      description: "Homework help and solutions.",
      hasIssue: true,
    },
    {
      id: 8,
      name: "And More",
      link: "#",
      description: "New tools coming soon.",
      comingSoon: true,
    },
  ];

  return (
    <ThemeProvider defaultTheme="dark">
      <DashboardContent
        services={services}
        userEmail={userEmail}
        userData={userData}
        profileDropdownOpen={profileDropdownOpen}
        setProfileDropdownOpen={setProfileDropdownOpen}
        setSettingsModalOpen={setSettingsModalOpen}
        setManageAccountModalOpen={setManageAccountModalOpen}
        ProfileDropdown={ProfileDropdown}
        SettingsModal={SettingsModal}
        ManageAccountModal={ManageAccountModal}
      />
    </ThemeProvider>
  );
}

function DashboardContent({
  services,
  userEmail,
  userData,
  profileDropdownOpen,
  setProfileDropdownOpen,
  setSettingsModalOpen,
  setManageAccountModalOpen,
  ProfileDropdown,
  SettingsModal,
  ManageAccountModal,
}) {
  const { theme, toggleTheme } = useTheme();
  const [appViewMode, setAppViewMode] = React.useState("standard");

  const sidebarTopItems = [
    { label: "Home", icon: LayoutGrid, active: true },
    { label: "Library", icon: BookOpen },
    { label: "Clips", icon: Scissors },
    { label: "Fonts", icon: Type },
    { label: "Notes", icon: FileText },
  ];

  const sidebarBottomItems = [
    { label: "Daily Streak", icon: Flame },
    { label: "Get a Free Month", icon: Gift },
    {
      label: "Settings",
      icon: Settings,
      onClick: () => setSettingsModalOpen(true),
    },
    { label: "Help", icon: HelpCircle },
  ];

  return (
    <SidebarProvider className="!min-h-0 h-screen overflow-hidden">
      {/* Sidebar */}
      <Sidebar className="border-r-0 [&_[data-sidebar=sidebar]]:border-r-0 [&>div:last-child]:border-r-0 [&_[data-sidebar=sidebar]]:!px-0 [&_[data-sidebar=sidebar]]:!py-0">
        <SidebarHeader className="!p-0 !px-5 !pt-[72px] !pb-1">
          <div className="flex items-center gap-3">
            <img
              src="/logos/edutoolswhitelogo.svg"
              alt="EduTools"
              className="h-7 w-auto dark:invert-0 invert"
            />
            {/* Dynamic status badge — reflects user's current payment tier */}
            <StatusBadge paymentStatus={userData?.paymentStatus} style={{ padding: "4px 10px", borderRadius: "6px" }} />
          </div>
        </SidebarHeader>
        <SidebarContent className="flex flex-col justify-between flex-1 !gap-[15px]">
          {/* Top nav items */}
          <SidebarGroup className="!pt-4">
            <SidebarGroupContent>
              <SidebarMenu className="!gap-[2px] !px-3">
                {sidebarTopItems.map((item) => (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton
                      className={`h-auto text-[14px] font-semibold !py-2 !pr-2 !rounded-lg !gap-2.5 text-gray-900 dark:text-gray-100 ${
                        item.active
                          ? "bg-sidebar-accent"
                          : "hover:bg-sidebar-accent"
                      }`}
                      onClick={item.onClick}
                    >
                      <item.icon size={20} strokeWidth={2.25} />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>

          {/* Bottom nav items */}
          <SidebarGroup className="!pb-3">
            <hr className="border-t border-sidebar-border mx-3 mb-2" />
            <SidebarGroupContent>
              <SidebarMenu className="!gap-[2px] !px-3">
                {sidebarBottomItems.map((item) => (
                  <SidebarMenuItem key={item.label}>
                    <SidebarMenuButton
                      className="h-auto text-[14px] font-semibold !py-2 !pr-2 !rounded-lg !gap-2.5 !overflow-visible [&>span:last-child]:!overflow-visible [&>span:last-child]:!text-clip text-gray-900 dark:text-gray-100 hover:bg-sidebar-accent"
                      onClick={item.onClick}
                    >
                      <item.icon size={20} strokeWidth={2.25} />
                      <span>{item.label}</span>
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                ))}
              </SidebarMenu>
            </SidebarGroupContent>
          </SidebarGroup>
        </SidebarContent>
      </Sidebar>

      {/* Main Content */}
      <SidebarInset className="!min-h-0 h-screen overflow-hidden">
        <div className="bg-background h-screen relative overflow-hidden flex flex-col">
          {/* Header Actions — right-aligned icons only */}
          <div className="flex items-center justify-end gap-1 px-5 pt-5 pb-2">
            {/* Theme Toggle */}
            <button
              onClick={toggleTheme}
              className="p-1.5 rounded-lg flex items-center justify-center hover:bg-accent"
              style={{ color: "#5A555C" }}
            >
              {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
            </button>

            {/* Notifications — Bell Bing icon from Figma */}
            <button
              className="p-1.5 rounded-lg flex items-center justify-center relative hover:bg-accent"
              style={{ color: "#5A555C" }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M19.34 14.49L18.34 12.83C18.13 12.46 17.94 11.76 17.94 11.35V8.82C17.94 6.47 16.56 4.44 14.57 3.49C14.05 2.57 13.09 2 11.99 2C10.9 2 9.92 2.59 9.4 3.52C7.45 4.49 6.1 6.5 6.1 8.82V11.35C6.1 11.76 5.91 12.46 5.7 12.82L4.69 14.49C4.29 15.16 4.2 15.9 4.45 16.58C4.69 17.25 5.26 17.77 6 18.02C7.94 18.68 9.98 19 12.02 19C14.06 19 16.1 18.68 18.04 18.03C18.74 17.8 19.28 17.27 19.54 16.58C19.8 15.89 19.73 15.13 19.34 14.49Z"
                  fill="currentColor"
                />
                <path
                  d="M14.83 20.01C14.41 21.17 13.3 22 12 22C11.21 22 10.43 21.68 9.88 21.11C9.56 20.81 9.32 20.41 9.18 20C9.31 20.02 9.44 20.03 9.58 20.05C9.81 20.08 10.05 20.11 10.29 20.13C10.86 20.18 11.44 20.21 12.02 20.21C12.59 20.21 13.16 20.18 13.72 20.13C13.93 20.11 14.14 20.09 14.34 20.06L14.83 20.01Z"
                  fill="currentColor"
                />
              </svg>
              {/* Red notification dot */}
              <span
                className="absolute top-1 right-1.5 h-2 w-2 rounded-full"
                style={{ backgroundColor: "#ED3C3C" }}
              />
            </button>

            {/* Profile — User Circle icon from Figma */}
            <div className="relative">
              <button
                onClick={() => setProfileDropdownOpen(!profileDropdownOpen)}
                className="p-1.5 rounded-lg flex items-center justify-center hover:bg-accent"
                style={{ color: "#5A555C" }}
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 5C13.66 5 15 6.34 15 8C15 9.66 13.66 11 12 11C10.34 11 9 9.66 9 8C9 6.34 10.34 5 12 5ZM12 19.2C9.5 19.2 7.29 17.92 6 15.98C6.03 13.99 10 12.9 12 12.9C13.99 12.9 17.97 13.99 18 15.98C16.71 17.92 14.5 19.2 12 19.2Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
              <ProfileDropdown />
            </div>
          </div>

          {/* Main Container */}
          <div className="bg-card border border-border rounded-lg ml-4 mr-4 mb-4 p-14 flex flex-col gap-14 flex-1 min-h-0 overflow-y-auto">
            {/* Welcome Row */}
            <div className="flex flex-col items-center">
              <h1 className="text-2xl font-bold text-foreground">
                Welcome back, {userData?.fullName?.split(" ")[0] || "User"}
              </h1>
            </div>

            {/* All Applications Section */}
            <div>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-bold text-foreground">All Apps</h2>
                <div className="flex items-center bg-gray-200 dark:bg-neutral-800 rounded-full p-1">
                  <button
                    onClick={() => setAppViewMode("standard")}
                    className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${appViewMode === "standard" ? "bg-gray-800 text-white dark:bg-gray-100 dark:text-gray-900" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}
                  >Standard</button>
                  <button
                    onClick={() => setAppViewMode("rows")}
                    className={`px-4 py-1.5 text-xs font-semibold rounded-full transition-colors ${appViewMode === "rows" ? "bg-gray-800 text-white dark:bg-gray-100 dark:text-gray-900" : "text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"}`}
                  >Rows</button>
                </div>
              </div>
              {appViewMode === "standard" ? (
                <div className="grid grid-cols-4 gap-4">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      className="group relative flex flex-col items-center justify-center rounded-2xl border border-border/60 bg-card hover:bg-accent/50 transition-all duration-200 cursor-pointer hover:-translate-y-1 hover:shadow-lg shadow-md"
                      style={{
                        height: "160px",
                        opacity:
                          service.underMaintenance || service.comingSoon
                            ? 0.5
                            : 1,
                        cursor:
                          service.underMaintenance || service.comingSoon
                            ? "not-allowed"
                            : "pointer",
                      }}
                      onClick={() =>
                        !service.underMaintenance &&
                        !service.comingSoon &&
                        window.open(service.link, "_blank")
                      }
                    >
                      {service.hasIssue && (
                        <Tooltip
                          title="Experiencing issues - we're working on it"
                          arrow
                          placement="top"
                          componentsProps={{
                            tooltip: {
                              sx: {
                                bgcolor: "rgba(239, 68, 68, 0.95)",
                                color: "#fff",
                                fontSize: "12px",
                                fontWeight: 500,
                                padding: "8px 12px",
                                borderRadius: "8px",
                                "& .MuiTooltip-arrow": {
                                  color: "rgba(239, 68, 68, 0.95)",
                                },
                              },
                            },
                          }}
                        >
                          <div className="absolute top-2 right-2 z-10 cursor-pointer">
                            <ErrorOutlineIcon
                              sx={{
                                color: "#ef4444",
                                fontSize: "20px",
                              }}
                            />
                          </div>
                        </Tooltip>
                      )}

                      {!service.comingSoon && (
                        <img
                          src={service.logo}
                          alt={`${service.name} Logo`}
                          className="w-14 h-14 mb-3 object-contain"
                        />
                      )}

                      {service.comingSoon && (
                        <div className="text-4xl font-light text-muted-foreground mb-3 leading-[56px]">
                          +
                        </div>
                      )}

                      <span className="text-sm font-semibold text-foreground">
                        {service.name}
                      </span>

                      {service.underMaintenance && (
                        <span className="mt-1.5 px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-500 border border-yellow-500/30">
                          Under Maintenance
                        </span>
                      )}
                      {service.comingSoon && (
                        <span className="mt-1.5 px-2 py-0.5 text-xs rounded bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  ))}

                  {/* Skeleton filler cards */}
                  {[...Array(8)].map((_, i) => (
                    <div
                      key={`skeleton-${i}`}
                      className="flex flex-col items-center justify-center rounded-2xl border border-border/40 bg-muted/30"
                      style={{ height: "160px" }}
                    >
                      <div className="w-14 h-14 mb-3 rounded-xl bg-muted/60 animate-pulse" />
                      <div className="w-16 h-3 rounded bg-muted/60 animate-pulse" />
                    </div>
                  ))}
                </div>
              ) : (
                /* Rows view */
                <div className="flex flex-col gap-2">
                  {services.map((service) => (
                    <div
                      key={service.id}
                      className="group relative flex items-center gap-4 rounded-xl border border-border/60 bg-card hover:bg-accent/50 transition-all duration-200 cursor-pointer px-4 py-3 shadow-sm hover:shadow-md"
                      style={{
                        opacity:
                          service.underMaintenance || service.comingSoon
                            ? 0.5
                            : 1,
                        cursor:
                          service.underMaintenance || service.comingSoon
                            ? "not-allowed"
                            : "pointer",
                      }}
                      onClick={() =>
                        !service.underMaintenance &&
                        !service.comingSoon &&
                        window.open(service.link, "_blank")
                      }
                    >
                      {!service.comingSoon ? (
                        <img
                          src={service.logo}
                          alt={`${service.name} Logo`}
                          className="w-10 h-10 object-contain rounded-lg"
                        />
                      ) : (
                        <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-muted text-2xl font-light text-muted-foreground">
                          +
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <span className="text-sm font-semibold text-foreground">{service.name}</span>
                        {service.description && (
                          <p className="text-xs text-muted-foreground truncate mt-0.5">{service.description}</p>
                        )}
                      </div>
                      {service.hasIssue && (
                        <ErrorOutlineIcon sx={{ color: "#ef4444", fontSize: "18px" }} />
                      )}
                      {service.underMaintenance && (
                        <span className="px-2 py-0.5 text-xs rounded bg-yellow-500/20 text-yellow-500 border border-yellow-500/30">
                          Maintenance
                        </span>
                      )}
                      {service.comingSoon && (
                        <span className="px-2 py-0.5 text-xs rounded bg-indigo-500/20 text-indigo-400 border border-indigo-500/30">
                          Coming Soon
                        </span>
                      )}
                    </div>
                  ))}

                  {/* Skeleton filler rows */}
                  {[...Array(6)].map((_, i) => (
                    <div
                      key={`skeleton-row-${i}`}
                      className="flex items-center gap-4 rounded-xl border border-border/40 bg-muted/30 px-4 py-3"
                    >
                      <div className="w-10 h-10 rounded-lg bg-muted/60 animate-pulse" />
                      <div className="flex-1 space-y-2">
                        <div className="w-24 h-3 rounded bg-muted/60 animate-pulse" />
                        <div className="w-40 h-2.5 rounded bg-muted/60 animate-pulse" />
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Tool suggestion CTA */}
              <p className="text-xs text-muted-foreground mt-16 mb-2 text-center">
                Want to see a tool added?{" "}
                <a
                  href="https://www.instagram.com/edutoo1s/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-semibold text-foreground hover:underline"
                >
                  Let us know
                </a>
              </p>
            </div>
          </div>
        </div>
      </SidebarInset>

      {/* Modals */}
      <SettingsModal />
      <ManageAccountModal />
    </SidebarProvider>
  );
}

export default HyperLinks;
